import { notify } from "../../components/Notification";
import {
  HoldingsSummary,
  TradeCommand,
  TradeWriteResult,
} from "../../services/holdings";
import {
  IRR_DEFINITION,
  isPricedValuation,
  PRE_TAX_GAIN_TOOLTIP,
  UNANNUALIZED_IRR_DEFINITION,
  VALUATION_PENDING_PLACEHOLDER,
  valuationAmountText,
} from "../../services/valuation";
import {
  capitalizeFirstLetter,
  DateFormat,
  formatCurrency,
  formatDate,
} from "../../utils";
import {
  LINKED_ACCOUNT_FALLBACK,
  MAX_QUOTE_FRACTION_DIGITS,
  SUB_UNIT_PRICE_THRESHOLD,
} from "./constants";
import { SelectOptionTypes } from "../../components/Input";
import {
  CashSettlement,
  InvestmentTrackingMode,
  Investments,
  InvestmentsDetails,
  ReturnBasis,
  UpdateInvestmentPayload,
} from "./types";

export const holdingOpenedText = (openedAt: string): string =>
  `opened ${formatDate(openedAt, DateFormat.DMY_SPACE)}`;

// Mirror of api/src/Api/Domain/PriceCoverage/horizon.ts — kept independent since the packages build separately.
export const PRICE_HISTORY_HORIZON_START = "2020-01-01";

// Both predicates answer `false` on an undefined tracking mode (the detail hook's first render) rather than
// defaulting a half-loaded account into either mode.
export const isLedgerMode = (
  mode: InvestmentTrackingMode | undefined | null,
): mode is "HOLDINGS_LEDGER" => mode === "HOLDINGS_LEDGER";

export const isLinkedSettlement = (
  investment: Pick<Investments, "trackingMode" | "cashSettlement">,
) =>
  isLedgerMode(investment.trackingMode) &&
  investment.cashSettlement === "LINKED_ACCOUNT";

export const linkedAccountNameOf = (
  investment: Pick<
    InvestmentsDetails,
    "trackingMode" | "cashSettlement" | "settlementAccountName"
  >,
): string | null =>
  isLinkedSettlement(investment)
    ? (investment.settlementAccountName ?? LINKED_ACCOUNT_FALLBACK)
    : null;

export const settlementPayloadOf = ({
  cashSettlement,
  settlementAccount,
}: {
  cashSettlement: CashSettlement;
  settlementAccount: SelectOptionTypes | null;
}): Pick<UpdateInvestmentPayload, "cashSettlement" | "settlementAccountId"> => {
  if (cashSettlement === "OWN_CASH" || !settlementAccount) {
    return { cashSettlement };
  }
  return {
    cashSettlement,
    settlementAccountId: Number(settlementAccount.value),
  };
};

export const settlementAccountOptionOf = ({
  settlementAccountId,
  settlementAccountName,
}: Pick<
  InvestmentsDetails,
  "settlementAccountId" | "settlementAccountName"
>): SelectOptionTypes | null =>
  settlementAccountId == null || settlementAccountName == null
    ? null
    : { value: String(settlementAccountId), label: settlementAccountName };

export const isManualMode = (
  mode: InvestmentTrackingMode | undefined | null,
): mode is "MANUAL_MARKET_UPDATES" => mode === "MANUAL_MARKET_UPDATES";

// Canonical `brokerValue` *is* the funded/settlement-only selection this used to reproduce, so it is now
// a read. Availability is the caller's job — `isPricedValuation` decides if it's shown.
export const presentedValueOf = (
  investment: Pick<Investments, "brokerValue">,
): number | null => investment.brokerValue;

// Portfolio-level counterpart of `presentedValueOf`, shared so the hero and the completeness gate can't
// disagree. `totalValue` keeps its own key here: portfolio scope has no canonical successor for it.
export const presentedTotalOf = (totals: {
  portfolioValue: number | null;
  totalValue: number | null;
}): number | null => totals.totalValue ?? totals.portfolioValue;

// Shared so the two heroes cannot word or gate this sentence differently; the formatter stays each
// screen's. Null when nothing can be decomposed: an unknown half, or a zero total with no ledger.
export const brokerCostsCaptionOf = (
  brokerCosts: number | null | undefined,
  accountFeeCosts: number | null | undefined,
  formatAmount: (value: number) => string,
): string | null => {
  if (brokerCosts == null || accountFeeCosts == null || brokerCosts === 0) {
    return null;
  }
  return `of which trading ${formatAmount(brokerCosts - accountFeeCosts)} · account ${formatAmount(accountFeeCosts)}`;
};

// The headline figure, whether it is showable, and its text — read together so a header cannot decide
// one of the three by a different rule than the other two.
export const accountSnapshotOf = (
  investment: Pick<Investments, "brokerValue" | "valuation">,
): { presentedValue: number | null; isPriced: boolean; valueText: string } => {
  const presentedValue = presentedValueOf(investment);
  const amount = { value: presentedValue, valuation: investment.valuation };
  return {
    presentedValue,
    isPriced: isPricedValuation(amount),
    valueText: valuationAmountText(amount),
  };
};

// A dash, not `VALUATION_PLACEHOLDER`: a figure the account simply has no split for is absent, not blocked.
export const currencyOrDash = (value: number | null | undefined): string =>
  value == null ? VALUATION_PENDING_PLACEHOLDER : formatCurrency(value);

// Signed over `formatCurrency`, not `formatSignedCurrency`, whose runtime locale would split the page's number format.
export const signedCurrencyOrDash = (
  value: number | null | undefined,
): string => {
  if (value == null) {
    return VALUATION_PENDING_PLACEHOLDER;
  }
  return `${value > 0 ? "+" : ""}${formatCurrency(value)}`;
};

export const openPositionsCaption = (openCount: number): string =>
  openCount === 1 ? "1 open position" : `${openCount} open positions`;

const hasCoverageGap = ({
  pricedPositionCount,
  openPositionCount,
}: HoldingsSummary): boolean => pricedPositionCount < openPositionCount;

export const coverageGapOf = (
  summary: HoldingsSummary | null,
): HoldingsSummary | null =>
  summary && hasCoverageGap(summary) ? summary : null;

export const pricedCoverageCaption = ({
  pricedPositionCount,
  openPositionCount,
}: HoldingsSummary): string =>
  `${pricedPositionCount} of ${openPositionsCaption(openPositionCount)} priced`;

const MANUAL_BASIS =
  "based on the value you type, after any tax the broker took.";

export const MANUAL_GAIN_TOOLTIP = capitalizeFirstLetter(MANUAL_BASIS);

const MIXED_GAIN_TOOLTIP = `${PRE_TAX_GAIN_TOOLTIP} Manual accounts: ${MANUAL_BASIS}`;

const LEDGER_RETURN_BASIS: ReturnBasis = {
  gainTooltip: PRE_TAX_GAIN_TOOLTIP,
};

const MANUAL_RETURN_BASIS: ReturnBasis = {
  gainTooltip: MANUAL_GAIN_TOOLTIP,
};

const MIXED_RETURN_BASIS: ReturnBasis = {
  gainTooltip: MIXED_GAIN_TOOLTIP,
};

// Null while no account has a known tracking mode, so nothing claims a basis for an empty set.
export const returnBasisOf = (
  investments: Pick<Investments, "trackingMode">[],
): ReturnBasis | null => {
  const hasLedger = investments.some(({ trackingMode }) =>
    isLedgerMode(trackingMode),
  );
  const hasManual = investments.some(({ trackingMode }) =>
    isManualMode(trackingMode),
  );
  if (hasLedger && hasManual) {
    return MIXED_RETURN_BASIS;
  }
  if (hasManual) {
    return MANUAL_RETURN_BASIS;
  }
  return hasLedger ? LEDGER_RETURN_BASIS : null;
};

export const NOT_ANNUALIZED_NOTE =
  "With under a year of history, this is the money-weighted return over the period so far rather than a yearly rate.";

export const NOT_ANNUALIZED_LABEL = `Not annualized. ${NOT_ANNUALIZED_NOTE}`;

const PERIOD_IRR_DEFINITION = `${UNANNUALIZED_IRR_DEFINITION} ${NOT_ANNUALIZED_NOTE}`;

const PERIOD_LIST_IRR_DEFINITION = `${UNANNUALIZED_IRR_DEFINITION} Returns marked with the info icon cover under a year and are not annualized.`;

// `=== false`, never falsy: a null or absent flag belongs to an absent IRR, which has nothing to qualify.
export const isPeriodIrr = (
  isIrrAnnualized: boolean | null | undefined,
): boolean => isIrrAnnualized === false;

// Takes the basis note, not a finished tooltip: a period rate needs its own definition, not the annualized one qualified.
export const irrTooltipOf = (
  basisNote: string,
  isIrrAnnualized: boolean | null | undefined,
): string => {
  const definition = isPeriodIrr(isIrrAnnualized)
    ? PERIOD_IRR_DEFINITION
    : IRR_DEFINITION;
  return `${definition} ${basisNote}`;
};

// A list qualifies only its tagged rows, so it cannot borrow the single-figure period definition.
export const irrListTooltipOf = (
  basisNote: string,
  hasPeriodRate: boolean,
): string =>
  hasPeriodRate
    ? `${PERIOD_LIST_IRR_DEFINITION} ${basisNote}`
    : irrTooltipOf(basisNote, true);

// Null rather than an empty string, so a caller cannot render a signed percentage for an absent IRR.
export const irrTextOf = (irr: number | null | undefined): string | null => {
  if (irr == null || !Number.isFinite(irr)) {
    return null;
  }
  return `${irr >= 0 ? "+" : ""}${irr.toFixed(1)}%`;
};

// Shared so the two account headers cannot word the same deactivation/last-updated ladder differently.
export const accountSubtitleOf = (
  investment: Pick<Investments, "isActive" | "deactivationDate">,
  lastUpdated: Date | null,
): string => {
  if (!investment.isActive && investment.deactivationDate) {
    return `Deactivated on ${formatDate(investment.deactivationDate, DateFormat.DMY_SPACE)}`;
  }
  if (lastUpdated) {
    return `Last updated ${formatDate(lastUpdated, DateFormat.DMY_SPACE)}`;
  }
  return "Not yet updated";
};

export const openedBeforePriceHistoryHorizon = (openedAt: string): boolean =>
  openedAt < PRICE_HISTORY_HORIZON_START;

// String manipulation only, never `Number()`/`parseFloat` — the column allows 22 integer digits, past what
// a double holds exactly, so parsing to trim would silently corrupt a large position.
export const trimQuantity = (quantity: string): string => {
  if (!quantity.includes(".")) {
    return quantity;
  }
  // Drop the fractional zeros, then the point itself if nothing survived it.
  return quantity.replace(/0+$/, "").replace(/\.$/, "");
};

// Sub-cent prices (e.g. crypto) would print as `0,00 €` under the standard 2-decimal format, so
// prices below the threshold show full precision instead.
export const formatUnitPrice = (price: number): string =>
  formatCurrency(price, {
    maximumFractionDigits:
      Math.abs(price) < SUB_UNIT_PRICE_THRESHOLD
        ? MAX_QUOTE_FRACTION_DIGITS
        : 2,
  });

export const isSettlementType = (type: TradeCommand["type"]): boolean =>
  type === "BUY_SETTLEMENT" || type === "SELL_SETTLEMENT";

// An account-level command names no position, so calling it a trade would misname a custody fee.
export const commandNoun = (
  command: Pick<TradeCommand, "holdingId">,
): string => (command.holdingId === null ? "entry" : "trade");

// Called after the success toast, never instead of it — a warning means the command *was* recorded, and
// swapping the order would read as a failure, which this channel must never do.
export const notifyLedgerCashWarnings = (
  result: TradeWriteResult,
  settlementAccountName: string | null = null,
): void => {
  for (const warning of result.warnings ?? []) {
    const balanceOn =
      `${formatCurrency(warning.projectedCash)} on ` +
      `${formatDate(warning.effectiveDate, DateFormat.DMY_SPACE)}`;
    if (warning.code === "NEGATIVE_CASH_BALANCE") {
      notify.warning(
        `Recorded. This leaves the account's cash at ${balanceOn} — ` +
          `record the deposit that funded it if you haven't yet.`,
      );
    } else if (warning.code === "NEGATIVE_SETTLEMENT_ACCOUNT_BALANCE") {
      notify.warning(
        `Recorded. This leaves ${settlementAccountName ?? LINKED_ACCOUNT_FALLBACK} at ${balanceOn}.`,
      );
    }
  }
};
